/**
 * The verdict footer — the executive deck's card grammar, as a board slot.
 *
 * Every card in the quarterly "IT & OT Security" deck
 * (`executive_leadership_meeting/content/deck.yaml`) ends in a `footer:` that is
 * not a caption but a **status verdict**: "We are holding. The pressure is not
 * going away.", "On track. No decision needed today." That single line is what
 * turns a wall of numbers into an argument, and it is the strongest thing the
 * board can borrow from the deck without borrowing its geometry.
 *
 * So it is a slot, not markup to copy. A panel passes a `Verdict`; the badge and
 * the sentence render the same way everywhere, and the tone comes from the
 * design system's reserved status tones rather than from a colour someone picked
 * (`.claude/rules/dashboard-design.md` §3.2). The badge is never colour-alone —
 * it carries the status word itself.
 */

import { Badge, type BadgeTone, Typography } from "@norican/react";
import type { ReactNode } from "react";

/** Status tones a verdict may carry — the reserved good/degraded/broken scale. */
export type VerdictTone = "success" | "warning" | "danger" | "neutral";

export interface Verdict {
  /**
   * The status word, rendered as a `Badge`: "Holding", "Watch", "Act",
   * "Reported". Keep it to one or two words — the sentence carries the detail.
   */
  label: string;
  /**
   * The verdict itself: one short sentence in the deck's voice, ending in what
   * it means rather than what it measures. "Holding. No decision needed."
   */
  text: ReactNode;
  /** Status tone. Defaults to `neutral`, which claims nothing. */
  tone?: VerdictTone;
}

/** `Badge` speaks the same status vocabulary; neutral is the honest fallback. */
function badgeTone(tone: VerdictTone | undefined): BadgeTone {
  switch (tone) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "danger":
      return "danger";
    default:
      return "neutral";
  }
}

export interface VerdictFooterProps {
  verdict: Verdict;
}

/**
 * A card's closing status line. Render inside a `Card.Footer` (or let
 * `ChartCard` / the Security board's `PanelCard` do it via their `verdict` prop).
 */
export function VerdictFooter({ verdict }: VerdictFooterProps) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <Badge tone={badgeTone(verdict.tone)} variant="subtle" size="sm">
        {verdict.label}
      </Badge>
      <Typography variant="body-sm" className="font-medium">
        {verdict.text}
      </Typography>
    </div>
  );
}
